import { useEffect, useRef, useState, type FormEvent } from "react";
import { ACADEMY_API_BASE, AcademyApiError, requestAcademy } from "../../../shared/academy-api";
import {
  announceSessionChange,
  AUTH_CHANGE_KEY,
  loadAcademySession,
  validateBootstrap,
  workspaceMatches,
  type AcademyBootstrap,
  type AcademyWorkspace,
  type VerifiedAudience,
} from "../../../shared/academy-session";
import { appUrl } from "../config/environment";
import academyLogo from "../../../shared/brand/chatoner-ai-academy-logo-landscape.webp?no-inline";
import { readAuthorizationClientId, readAuthorizationRequest } from "../services/authorization";
import "../styles/secure-access.css";

const emptySession: AcademyBootstrap = { user: null, workspaces: [] };
function audienceFor(workspace: AcademyWorkspace): VerifiedAudience | null {
  if (workspaceMatches(workspace, "learners")) return "learners";
  if (workspaceMatches(workspace, "parents")) return "parents";
  return null;
}
function accountReady(session: AcademyBootstrap) {
  return Boolean(session.user?.emailVerified && !session.requiresMfa && !session.user.mfaRequired);
}

export default function SecureAccessApp() {
  const [clientId] = useState(readAuthorizationClientId);
  const [authorization] = useState(readAuthorizationRequest);
  const [session, setSession] = useState<AcademyBootstrap>(emptySession);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const running = useRef(false);
  const version = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const csrf = useRef<string | undefined>(undefined);
  const query = new URLSearchParams(window.location.search);
  const invalidRequest = Boolean(
    (query.has("return_to") || query.has("state")) &&
    (!authorization ||
      !["learners", "parents"].includes(authorization.clientId) ||
      new URL(authorization.returnTo).username ||
      new URL(authorization.returnTo).password),
  );
  const unsupportedPath =
    /\/(?:sign-up|signup|forgot-password|reset-password|verify-email|mfa|invite|onboarding|complete)\/?$/.test(
      window.location.pathname,
    ) || window.location.pathname.startsWith("/apply");

  function accept(result: unknown) {
    const verified = validateBootstrap(result);
    csrf.current = verified.csrfToken;
    setSession(verified);
    if (verified.user && !accountReady(verified))
      setError(
        "Your account still requires verification. Contact Academy support before opening a workspace.",
      );
    return verified;
  }
  async function checkSession() {
    const revision = ++version.current;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    setError("");
    try {
      const result = await loadAcademySession(controller.signal);
      if (revision === version.current && !controller.signal.aborted) accept(result);
    } catch (cause) {
      if (revision !== version.current || controller.signal.aborted) return;
      setSession(emptySession);
      if (!(cause instanceof AcademyApiError && cause.status === 401))
        setError(cause instanceof Error ? cause.message : "Academy could not check your session.");
    } finally {
      if (revision === version.current) setLoading(false);
    }
  }
  useEffect(() => {
    void checkSession();
    const externalChange = (event: StorageEvent) => {
      if (event.key !== AUTH_CHANGE_KEY) return;
      version.current++;
      abort.current?.abort();
      running.current = false;
      setBusy(false);
      setLoading(false);
      setSession(emptySession);
      setError("Your account changed in another tab. Check your session before continuing.");
    };
    window.addEventListener("storage", externalChange);
    return () => {
      // This counter invalidates asynchronous work; it is not a DOM element ref.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      version.current++;
      abort.current?.abort();
      window.removeEventListener("storage", externalChange);
    };
    // The initial session check is intentionally replaced only by explicit account operations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (running.current || logoutPending || invalidRequest) return;
    running.current = true;
    const revision = ++version.current;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setBusy(true);
    setError("");
    setSession(emptySession);
    announceSessionChange();
    try {
      const result = await requestAcademy(ACADEMY_API_BASE, "auth/password", {
        body: { email: email.trim(), password, remember },
        signal: controller.signal,
      });
      if (revision !== version.current || controller.signal.aborted) return;
      const verified = accept(result);
      if (!verified.user) throw new Error("Academy did not confirm this sign-in. Please try again.");
    } catch (cause) {
      if (revision === version.current && !controller.signal.aborted)
        setError(cause instanceof Error ? cause.message : "Sign-in could not complete.");
    } finally {
      if (revision === version.current) {
        setPassword("");
        setBusy(false);
        running.current = false;
      }
    }
  }
  async function signOut() {
    if (running.current) return;
    running.current = true;
    const revision = ++version.current;
    abort.current?.abort();
    setSession(emptySession);
    setPassword("");
    setError("");
    setLogoutPending(true);
    setBusy(true);
    announceSessionChange();
    try {
      await requestAcademy(ACADEMY_API_BASE, "auth/logout", { body: {}, csrfToken: csrf.current });
      if (revision !== version.current) return;
      csrf.current = undefined;
      setLogoutPending(false);
    } catch (cause) {
      if (revision === version.current)
        setError(cause instanceof Error ? cause.message : "Sign-out could not be confirmed.");
    } finally {
      if (revision === version.current) {
        setBusy(false);
        running.current = false;
      }
    }
  }
  async function openWorkspace(workspace: AcademyWorkspace) {
    const audience = audienceFor(workspace);
    if (running.current || invalidRequest || !accountReady(session) || !audience || !session.user) return;
    running.current = true;
    const revision = ++version.current;
    const expectedSubject = session.user.id;
    setBusy(true);
    setError("");
    announceSessionChange();
    try {
      const selected = validateBootstrap(
        await requestAcademy(ACADEMY_API_BASE, "session/workspace", {
          body: { workspaceId: workspace.id },
          csrfToken: session.csrfToken,
        }),
      );
      if (revision !== version.current) return;
      if (
        !selected.user ||
        selected.user.id !== expectedSubject ||
        !accountReady(selected) ||
        selected.selectedWorkspaceId !== workspace.id ||
        !selected.workspaces.some(item => item.id === workspace.id && workspaceMatches(item, audience))
      ) {
        setSession(emptySession);
        throw new Error("Your account or assignment changed. Check your session before continuing.");
      }
      accept(selected);
      const destination = new URL(authorization ? authorization.returnTo : appUrl(audience));
      destination.searchParams.delete("auth_code");
      if (authorization) {
        destination.searchParams.set("auth", "verified");
        destination.searchParams.set("state", authorization.state);
      }
      window.location.assign(destination.toString());
    } catch (cause) {
      if (revision === version.current)
        setError(cause instanceof Error ? cause.message : "This workspace could not open.");
    } finally {
      if (revision === version.current) {
        setBusy(false);
        running.current = false;
      }
    }
  }
  const workspaces = session.workspaces.filter(workspace => {
    const audience = audienceFor(workspace);
    return audience && (!clientId || clientId === audience);
  });
  const signedIn = Boolean(session.user);
  return (
    <main className="secure-access" data-testid="academy-secure-access">
      <header className="secure-access-header">
        <a href="/auth/sign-in" aria-label="Chatoner AI Academy Access">
          <img src={academyLogo} alt="Chatoner AI Academy" width="256" height="72" />
        </a>
        <span>Academy Access</span>
      </header>
      <div className="secure-access-layout">
        <section className="secure-access-story" aria-label="Academy learning">
          <img
            className="secure-access-art"
            src="/images/academy/auth-magic-library-v3.webp"
            alt=""
            width="960"
            height="960"
          />
          <div>
            <p className="secure-access-eyebrow">A place to learn and build</p>
            <h1>
              Your Academy. <br />
              Your next step.
            </h1>
            <p>Open your learning or family workspace with your Academy account.</p>
          </div>
        </section>
        <section className="secure-access-panel" aria-busy={busy || loading}>
          <p className="secure-access-eyebrow">
            {clientId === "parents"
              ? "Parent & guardian access"
              : clientId === "learners"
                ? "Learner access"
                : "Welcome to Academy"}
          </p>
          <h2>
            {loading
              ? "Checking your session…"
              : signedIn
                ? "Choose your workspace"
                : logoutPending
                  ? "Confirming sign-out"
                  : "Sign in to your account"}
          </h2>
          {invalidRequest ? (
            <div role="alert" className="secure-access-error">
              This sign-in return is invalid. <a href="/auth/sign-in">Open Academy Access</a> to choose your
              workspace.
            </div>
          ) : null}
          {error && (
            <div role="alert" className="secure-access-error">
              {error}
            </div>
          )}
          {loading ? (
            <p role="status">Checking your account with Academy.</p>
          ) : logoutPending ? (
            <>
              <p>Your account view is locked until the server confirms sign-out.</p>
              <button type="button" disabled={busy} onClick={() => void signOut()}>
                {busy ? "Signing out…" : "Retry sign out"}
              </button>
            </>
          ) : signedIn ? (
            <>
              <p className="secure-access-account">
                Signed in as <strong>{session.user?.name}</strong>
                <span>{session.user?.email}</span>
              </p>
              {accountReady(session) && workspaces.length > 0 ? (
                <div className="secure-access-workspaces">
                  {workspaces.map(workspace => (
                    <article key={workspace.id}>
                      <span>{audienceFor(workspace) === "parents" ? "Parent & guardian" : "Learner"}</span>
                      <h3>{workspace.name}</h3>
                      <button
                        type="button"
                        disabled={busy || invalidRequest}
                        onClick={() => void openWorkspace(workspace)}
                      >
                        Open {workspace.name}
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <p>
                  No active{" "}
                  {clientId === "parents"
                    ? "parent or guardian"
                    : clientId === "learners"
                      ? "learner"
                      : "learner or parent"}{" "}
                  workspace is available for this account. Ask your Academy administrator to check your
                  assignment.
                </p>
              )}
              <button
                type="button"
                className="secure-access-secondary"
                disabled={busy}
                onClick={() => void signOut()}
              >
                Sign out
              </button>
            </>
          ) : unsupportedPath ? (
            <>
              <p>
                Account registration, recovery and verification are not available through this service yet.
                Contact your Academy administrator for account access.
              </p>
              <a className="secure-access-link" href={`/auth/sign-in${window.location.search}`}>
                Back to sign in
              </a>
            </>
          ) : (
            <>
              <p>Use the email and password assigned to your Academy account.</p>
              <form onSubmit={event => void signIn(event)}>
                <label htmlFor="academy-email">Email address</label>
                <input
                  id="academy-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  maxLength={254}
                  value={email}
                  disabled={busy}
                  onChange={event => setEmail(event.target.value)}
                />
                <label htmlFor="academy-password">Password</label>
                <div className="secure-access-password">
                  <input
                    id="academy-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    maxLength={128}
                    value={password}
                    disabled={busy}
                    onChange={event => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="secure-access-reveal"
                    onClick={() => setShowPassword(value => !value)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <label className="secure-access-remember">
                  <input
                    type="checkbox"
                    checked={remember}
                    disabled={busy}
                    onChange={event => setRemember(event.target.checked)}
                  />
                  Keep me signed in
                </label>
                <button type="submit" disabled={busy || invalidRequest}>
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>
              <p className="secure-access-help">
                Need an account or a password reset? Contact your Academy administrator.
              </p>
              {error && (
                <button
                  type="button"
                  className="secure-access-secondary"
                  disabled={busy}
                  onClick={() => void checkSession()}
                >
                  Check session again
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
